import DropdownIcon from "@/assets/4D64FC_dropdown_icon.svg?react";
import BlueTickIcon from "@/assets/4D64FC_tick.svg?react";
import InfoIcon from "@/assets/colorable/info_filled.svg?react";
import InputField from "@/components/Common/forms/InputField";
import ReusableDropdown from "@/components/Common/forms/ReusableDropdown";
import SegmentedToggle from "@/components/Common/forms/SegmentedToggle";
import TextareaWithLinks from "@/components/Common/forms/TextareaWithLinks";
import type {ContentReviewTemplateTranslationKeySerializer} from "@/generated/ContentReview";
import clsxm from "@/lib/clsxm";
import {OTHER_TEMPLATE_ID} from "@/store/constants";
import {
  templateLanguageAtom,
  type contentReviewTemplateFormAtom,
  type disciplineTemplateFormAtom,
} from "@/store/jotaiAtoms";
import type {TLanguage, TTemplateCustomContent} from "@/store/types";
import type {WritableDraft} from "immer";
import {useAtom, type WritableAtom} from "jotai";
import type {RESET} from "jotai/utils";
import {memo, useCallback, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";

const TemplateForm: FC<{
  contentId?: number;
  customContentAtom: WritableAtom<
    TTemplateCustomContent,
    [TTemplateCustomContent | typeof RESET | ((draft: WritableDraft<TTemplateCustomContent>) => void)],
    void
  >;
  data?: ContentReviewTemplateTranslationKeySerializer[];
  descriptionPlaceholderKey?: string;
  hideFormConfig?: boolean;
  isDisabled?: boolean;
  isEmptyTemplate?: boolean;
  isFetching?: boolean;
  queryRef(node?: Element | null | undefined): void;
  showCustomContent?: boolean;
  subjectPlaceholderKey?: string;
  templateFormAtom: typeof contentReviewTemplateFormAtom | typeof disciplineTemplateFormAtom;
}> = ({
  contentId,
  customContentAtom,
  data,
  descriptionPlaceholderKey,
  hideFormConfig,
  isDisabled,
  isFetching,
  isEmptyTemplate,
  queryRef,
  showCustomContent,
  subjectPlaceholderKey,
  templateFormAtom,
}) => {
  const {t} = useTranslation();
  const [feedbackLng, setFeedbackLng] = useAtom(templateLanguageAtom);

  const [template, setTemplate] = useAtom(templateFormAtom);
  const [customContent, setCustomContent] = useAtom(customContentAtom);

  const onClickFeedbackLanguage = useCallback(
    (language: TLanguage) => {
      setFeedbackLng(language);
      setCustomContent(state => {
        state.feedback = template.translations.find(t => t.language_code === language)?.body!;
        state.subject = template.translations.find(t => t.language_code === language)?.title!;
      });
    },
    [setCustomContent, template.translations],
  );

  const selectedTemplate = useMemo(() => {
    if (isDisabled) {
      return {
        value: customContent?.template_id?.toString()!,
        label: customContent?.template_name,
      };
    }

    const t = data?.find(t => t.id === template.id) || data?.[0];

    if (t === undefined) {
      return {
        value: template.id?.toString()!,
        label: template.name!,
      };
    }

    return {
      value: t?.id?.toString()!,
      label: t?.name!,
    };
  }, [template, data, customContent, isDisabled]);

  const templates = useMemo(
    () =>
      data?.map(t => {
        return {
          value: t.id?.toString()!,
          label: t?.name!,
        };
      }),
    [data],
  );

  return (
    <div className="flex flex-col gap-5">
      {!hideFormConfig && (
        <ReusableDropdown
          setSelectedOption={option => {
            let template = data?.find(t => t.id?.toString()! === option.value);

            if (isEmptyTemplate) {
              template = data?.[0];
            }

            setTemplate(template!);
            setCustomContent(state => {
              state.template_id = template?.id!;
              state.template_name = template?.name!;
              state.subject = template?.translations.find(t => t.language_code === feedbackLng)?.title!;
              state.feedback = template?.translations.find(t => t.language_code === feedbackLng)?.body!;
            });
          }}
          selectedOption={selectedTemplate}
          options={templates ?? []}
          isDisabled={isDisabled}
          label={t("choose_template")}
          labelClassName="text-xs text-neutral-black font-normal"
          dropdownClassName="max-h-[320px] overflow-y-auto"
          isAsync
          isFetching={isFetching}
          queryRef={queryRef}
          renderButton={({selectedOption, isOpen, className, ...rest}) => (
            <button
              {...rest}
              className={clsxm(
                className,
                isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light",
                selectedOption ? "text-neutral-black font-medium" : "text-neutral-grey-light",
                "gap-2",
              )}>
              <div className="ml-3 flex grow items-center gap-2">
                <span className="text-neutral-black leading-none">{selectedOption?.label || t("choose_template")}</span>
              </div>
              <DropdownIcon
                className={clsxm(
                  "mr-4 size-3.5 shrink-0 transition-transform duration-250",
                  isOpen ? "rotate-180" : "rotate-0",
                )}
              />
            </button>
          )}
          renderItem={({option, onClick, isHighlighted, className, isSelected, key, ...rest}) => (
            <li
              key={option.value}
              onClick={onClick}
              className={clsxm(
                className,
                option.value === OTHER_TEMPLATE_ID.toString()
                  ? "bg-primary-light-solid"
                  : "hover:bg-neutral-white-dark",
                "gap-2",
              )}
              {...rest}
              value={option.value}>
              <span className="text-neutral-black grow leading-none">{t(option.label)}</span>
              {isSelected && <BlueTickIcon />}
            </li>
          )}
        />
      )}
      {!!selectedTemplate.value && (
        <>
          <>
            {!isDisabled && (
              <div className="flex flex-col gap-1">
                <div className="text-neutral-grey-dark flex items-center gap-2">
                  <InfoIcon className="size-3.5" />
                  <span className="text-[10px]">{t("language_switch_hint")}</span>
                </div>
                <div className="flex items-center self-stretch">
                  <SegmentedToggle
                    options={[
                      {label: "german", onClick: () => onClickFeedbackLanguage("de_CH")},
                      {label: "english", onClick: () => onClickFeedbackLanguage("en_EN")},
                    ]}
                    selectedIndex={feedbackLng === "de_CH" ? 0 : 1}
                  />
                </div>
              </div>
            )}
            {!hideFormConfig && template.id === OTHER_TEMPLATE_ID && !isDisabled && (
              <div>
                <div className="flex items-center gap-1">
                  <label className="text-primary-dark-solid border-primary-light-solid rounded-full border px-2.5 text-[10px] font-semibold capitalize">
                    {t("optional")}
                  </label>
                  <label className="text-neutral-black text-xs">{t("template_title")}</label>
                </div>
                <InputField
                  value={template.name}
                  isDisabled={isDisabled}
                  onChange={value => {
                    setTemplate(state => {
                      if (!state) return state;
                      state.name = value;
                      return state;
                    });

                    setCustomContent(state => {
                      state.template_name = value;
                    });
                  }}
                  placeholder={t("template_title_placeholder")}
                  className="border-primary-light-solid"
                  labelClassName="text-xs font-normal text-neutral-black"
                />
              </div>
            )}
          </>

          <InputField
            value={
              showCustomContent
                ? customContent.subject
                : template?.translations.find(t => t.language_code === feedbackLng)?.title
            }
            isDisabled={isDisabled}
            onChange={value => {
              setTemplate(state => {
                if (!state) return state;

                // Switch to custom template if user changes the content
                state.id = OTHER_TEMPLATE_ID;

                state.translations.find(tr => tr.language_code === feedbackLng)!.title = value;
                return state;
              });

              if (template?.id === OTHER_TEMPLATE_ID) {
                setCustomContent(state => {
                  state.subject = value;
                });
              }
            }}
            label={t("feedback_subject")}
            placeholder={t(subjectPlaceholderKey ?? "feedback_subject_placeholder")}
            className="border-primary-light-solid"
            labelClassName="text-xs font-normal text-neutral-black"
          />
          <div className="flex w-full flex-col">
            <label className="text-neutral-black text-xs font-normal">{t("feedback_description")}</label>
            <TextareaWithLinks
              key={`${contentId}-${template?.id}-${feedbackLng}`}
              value={
                showCustomContent
                  ? customContent.feedback
                  : template?.translations.find(t => t.language_code === feedbackLng)?.body!
              }
              disabled={isDisabled}
              onChange={value => {
                setTemplate(state => {
                  if (!state) return state;

                  // Switch to custom template if user changes the content
                  state.id = OTHER_TEMPLATE_ID;

                  state.translations.find(tr => tr.language_code === feedbackLng)!.body = value;
                  return state;
                });

                if (template?.id === OTHER_TEMPLATE_ID) {
                  setCustomContent(state => {
                    state.feedback = value;
                  });
                }
              }}
              placeholder={t(descriptionPlaceholderKey ?? "feedback_description_placeholder")}
              className="p-4"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default memo(TemplateForm);
